/**
 * The Bulk sends tab: one email to a segment, now or at a set time. Each is
 * an automation underneath (a send-once schedule and a Send email step,
 * marked `bulk_send`), so it gets consent, the never-email list, one email
 * per address, the address check, limits and warm-up for free. They are
 * listed here rather than on the Automations page.
 *
 * A row's status comes from the automation's send-once schedule; its
 * figures from the Emails overview's per-automation totals (`wf-<id>`),
 * over the last 90 days.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Alert, Badge, Group, Loader, Menu, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconDots, IconTrash } from '@tabler/icons-react';
import { useEmailStats } from '../../api/queries';
import { useWorkflowEdits, useWorkflows, type WorkflowSummary } from '../../api/workflows';
import { formatNumber } from '../../theme';
import { onceWords } from '../workflows/sendOnce';

/** Where a bulk send stands, as a badge and a line under it. */
export function bulkStatus(w: Pick<WorkflowSummary, 'send_once' | 'archived'>): { label: string; color: string; detail?: string } {
  const s = w.send_once;
  if (w.archived) return { label: 'Archived', color: 'gray' };
  if (!s) return { label: 'Draft', color: 'gray', detail: 'Not scheduled yet' };
  if (s.state === 'sending') {
    return { label: 'Sending', color: 'teal', detail: `${formatNumber(s.done)} of ${formatNumber(s.total)}` };
  }
  if (s.state === 'sent') return { label: 'Sent', color: 'gray', detail: `To ${formatNumber(s.total)}` };
  if (s.waiting_for_check) return { label: 'Needs a check', color: 'chartAmber', detail: `${onceWords(s.at)}, once its addresses are checked` };
  return { label: 'Scheduled', color: 'blue', detail: onceWords(s.at) };
}

function pct(n: number, of: number): string {
  if (!of) return '-';
  const p = (n / of) * 100;
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
}

export function BulkSendsTab() {
  const navigate = useNavigate();
  const list = useWorkflows();
  const stats = useEmailStats('90d');
  const edits = useWorkflowEdits();
  const [busy, setBusy] = useState<string | null>(null);
  const sends = (list.data?.workflows ?? []).filter((w) => w.bulk_send);
  const canManage = !!list.data?.can_manage;
  const totals = new Map((stats.data?.campaigns ?? []).filter((c) => c.workflow_id).map((c) => [c.workflow_id!, c.totals]));

  async function duplicate(id: string) {
    setBusy(id);
    try {
      const copy = await edits.duplicate.mutateAsync(id);
      navigate(`/emails/bulk/${copy.id}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not duplicate', message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function remove(w: WorkflowSummary) {
    setBusy(w.id);
    try {
      // One that has sent keeps its history: archived, not deleted.
      if (w.total_runs > 0) await edits.archive.mutateAsync(w.id);
      else await edits.remove.mutateAsync(w.id);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not remove', message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" maw={640}>
        One email to a segment, now or at a time you choose. Only people who agreed to emails get it, once each,
        newest first, with the never-email list left out and the addresses checked before it goes.
      </Text>

      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      {list.isPending ? (
        <Group justify="center" py="xl"><Loader size="sm" /></Group>
      ) : sends.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl" data-testid="no-bulk-sends">
          No bulk sends yet. Make one to email a segment, such as homeowners from the last three months.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing={8} highlightOnHover data-testid="bulk-sends">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Sent</Table.Th>
                <Table.Th ta="right">Opened</Table.Th>
                <Table.Th ta="right">Clicked</Table.Th>
                <Table.Th ta="right">Unsubscribed</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sends.map((w) => {
                const st = bulkStatus(w);
                const t = totals.get(w.id);
                const base = t ? (t.delivered > 0 ? t.delivered : t.sent) : 0;
                return (
                  <Table.Tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/emails/bulk/${w.id}`)} data-testid={`bulk-row-${w.id}`}>
                    <Table.Td><Text size="sm" fw={600}>{w.name}</Text></Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={st.color}>{st.label}</Badge>
                      {st.detail && <Text size="xs" c="dimmed" mt={2}>{st.detail}</Text>}
                    </Table.Td>
                    <Table.Td ta="right">{t ? formatNumber(t.sent) : '-'}</Table.Td>
                    <Table.Td ta="right">{t ? pct(t.opened, base) : '-'}</Table.Td>
                    <Table.Td ta="right">{t ? pct(t.clicked, base) : '-'}</Table.Td>
                    <Table.Td ta="right">{t ? pct(t.unsubscribed, base) : '-'}</Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      {canManage && (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" loading={busy === w.id} aria-label={`More for ${w.name}`}>
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => void duplicate(w.id)}>
                              Duplicate, to send again
                            </Menu.Item>
                            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => void remove(w)}>
                              {w.total_runs > 0 ? 'Archive' : 'Delete'}
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Text size="xs" c="dimmed">Figures are for the last 90 days. Rates are of delivered emails.</Text>
    </Stack>
  );
}
